import React, { useState, useCallback, useMemo } from "react";
import { NotificationContext, type NotificationContextValue } from "~/contexts/notificationContext";
import type { Notification, RollResult, TagAction, RestNotification } from "~/types/character";
import { generateUUID } from "~/utils/uuid";

export interface NotificationProviderProps {
  children: React.ReactNode;
}

/**
 * NotificationProvider Component
 *
 * Provides notification and roll history state management to the entire application.
 * Should be placed high in the component tree (typically in App.tsx).
 */
export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [rollHistory, setRollHistory] = useState<RollResult[]>([]);

  const addRollNotification = useCallback((roll: RollResult) => {
    setRollHistory((prev) => [roll, ...prev]);
    setNotifications((prev) => [{ kind: "roll", data: roll }, ...prev]);
  }, []);

  const addTagNotification = useCallback((action: TagAction) => {
    const notification: Notification = {
      kind: "tag",
      data: {
        id: generateUUID(),
        type: action.type,
        value: action.value,
        sourceName: action.sourceName,
        sourceType: action.sourceType,
      },
    };
    setNotifications((prev) => [notification, ...prev]);
  }, []);

  const addRestNotification = useCallback((rest: RestNotification) => {
    const notification: Notification = {
      kind: "rest",
      data: rest,
    };
    setNotifications((prev) => [notification, ...prev]);
  }, []);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.data.id !== id));
  }, []);

  const clearRoll = useCallback((id: string) => {
    setRollHistory((prev) => prev.filter((roll) => roll.id !== id));
  }, []);

  const clearAllRolls = useCallback(() => {
    setRollHistory([]);
  }, []);

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications,
      rollHistory,
      addRollNotification,
      addTagNotification,
      addRestNotification,
      dismissNotification,
      clearRoll,
      clearAllRolls,
    }),
    [
      notifications,
      rollHistory,
      addRollNotification,
      addTagNotification,
      addRestNotification,
      dismissNotification,
      clearRoll,
      clearAllRolls,
    ]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};
